<template>
  <div class="flex min-h-[24rem] w-full max-w-xl flex-col gap-6">
    <div>
      <h1 class="text-2xl font-semibold">
        {{ contactConfirmed ? t('confirmedTitle') : t('title') }}
      </h1>
      <p class="mt-2 text-base text-gray-600">
        {{ contactConfirmed ? t('confirmedSubtitle') : t('subtitle') }}
      </p>
    </div>

    <div v-if="showForm" class="contact-fields space-y-5">
      <Input v-model="email" size="lg" :label="t('email')" inputmode="email" autocomplete="off" />
      <Input
        v-model="phone"
        size="lg"
        type="tel"
        :label="t('phone')"
        inputmode="tel"
        autocomplete="off"
      />
    </div>

    <div v-else class="contact-confirmed space-y-5">
      <LabeledText :label="t('email')">
        <span class="text-lg">{{ email }}</span>
      </LabeledText>
      <LabeledText :label="t('phone')">
        <span class="text-lg">{{ phone }}</span>
      </LabeledText>
    </div>

    <div class="mt-auto w-full">
      <CVButton
        v-if="showForm"
        class="w-full"
        variant="default"
        size="lg"
        :is-loading="isSubmitting"
        @click="handleConfirm"
      >
        <CheckIcon class="size-6" />
        {{ t('confirm') }}
      </CVButton>
      <CVButton
        v-else
        class="w-full"
        variant="secondary"
        size="lg"
        :is-loading="isSubmitting"
        @click="handleEdit"
      >
        <PencilIcon class="size-6" />
        {{ t('edit') }}
      </CVButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import CVButton from '@/components/buttons/CVButton.vue';
import Input from '@/components/Input.vue';
import LabeledText from '@/components/LabeledText.vue';
import { counterProcessSessionService } from '@/api/counter-process-session.service';
import { CheckIcon, PencilIcon } from 'lucide-vue-next';
import { useI18n } from 'vue-i18n';
import { computed, ref, watch } from 'vue';

const props = defineProps<{
  counterId: string;
  email: string;
  phone: string;
  contactConfirmed?: boolean;
}>();

const emit = defineEmits<{
  (e: 'confirmed'): void;
}>();

const { t } = useI18n();
const email = ref(props.email);
const phone = ref(props.phone);
const isSubmitting = ref(false);

const showForm = computed(() => !props.contactConfirmed);

watch(
  () => [props.email, props.phone],
  ([newEmail, newPhone]) => {
    email.value = newEmail;
    phone.value = newPhone;
  },
);

const handleEdit = async () => {
  if (isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    await counterProcessSessionService.unconfirmContact(props.counterId);
    emit('confirmed');
  } finally {
    isSubmitting.value = false;
  }
};

const handleConfirm = async () => {
  if (isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    await counterProcessSessionService.confirmContact(props.counterId, {
      email: email.value,
      phone: phone.value,
    });
    emit('confirmed');
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.contact-fields :deep(h4),
.contact-confirmed :deep(h4) {
  @apply text-base;
}
</style>

<i18n lang="json">
{
  "en": {
    "title": "Confirm your contact details",
    "subtitle": "Please check your email and phone number.",
    "confirmedTitle": "Contact details confirmed",
    "confirmedSubtitle": "Your email and phone number have been saved.",
    "email": "Email",
    "phone": "Phone",
    "confirm": "Confirm",
    "edit": "Edit"
  },
  "de": {
    "title": "Kontaktdaten bestätigen",
    "subtitle": "Bitte prüfen Sie Ihre E-Mail-Adresse und Telefonnummer.",
    "confirmedTitle": "Kontaktdaten bestätigt",
    "confirmedSubtitle": "Ihre E-Mail-Adresse und Telefonnummer wurden gespeichert.",
    "email": "E-Mail",
    "phone": "Telefon",
    "confirm": "Bestätigen",
    "edit": "Bearbeiten"
  },
  "es": {
    "title": "Confirme sus datos de contacto",
    "subtitle": "Compruebe su correo electrónico y número de teléfono.",
    "confirmedTitle": "Datos de contacto confirmados",
    "confirmedSubtitle": "Su correo y teléfono se han guardado.",
    "email": "Correo electrónico",
    "phone": "Teléfono",
    "confirm": "Confirmar",
    "edit": "Editar"
  }
}
</i18n>
